import { motion } from 'framer-motion';
import { Heart, Fish, Ruler, Pause, Zap } from 'lucide-react';

interface GameHUDProps {
  score: number;
  health: number;
  maxHealth: number;
  distance?: number;
  combo?: number;
  powerUp?: string | null;
  powerUpTimeLeft?: number;
  onPause: () => void;
  variant?: 'river-rush' | 'rescue';
}

export function GameHUD({
  score,
  health,
  maxHealth,
  distance,
  combo,
  powerUp,
  powerUpTimeLeft,
  onPause,
  variant = 'river-rush',
}: GameHUDProps) {
  return (
    <div className="absolute top-0 left-0 right-0 p-2 sm:p-3 flex items-center justify-between pointer-events-none z-10">
      {/* Left side - Health */}
      <div className="flex items-center gap-0.5 sm:gap-1 bg-black/50 rounded-full px-2 sm:px-3 py-1 sm:py-1.5 pointer-events-auto">
        {Array.from({ length: maxHealth }).map((_, i) => (
          <Heart
            key={i}
            className={`w-4 h-4 sm:w-5 sm:h-5 ${
              i < health
                ? 'text-red-500 fill-red-500'
                : 'text-gray-600 fill-gray-600'
            }`}
          />
        ))}
      </div>

      {/* Center - Score (prominent) and Distance/Combo inline on landscape */}
      <div className="flex flex-col landscape:flex-row items-center gap-1 landscape:gap-2">
        {/* Score display - smaller on landscape */}
        <div className="bg-gradient-to-r from-yellow-600 to-amber-500 rounded-full px-3 sm:px-5 py-1 sm:py-2 shadow-lg">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <Fish className="w-4 h-4 sm:w-6 sm:h-6 text-yellow-200" />
            <span className="text-white font-bold text-base sm:text-xl drop-shadow-md">
              {score.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Distance - inline on landscape */}
        {distance !== undefined && (
          <div className="flex items-center gap-1 sm:gap-1.5 bg-black/40 rounded-full px-2 sm:px-3 py-0.5 sm:py-1">
            <Ruler className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400" />
            <span className="text-white/90 font-medium text-xs sm:text-sm">
              {Math.floor(distance)}m
            </span>
          </div>
        )}

        {/* Combo display - inline on landscape */}
        {combo !== undefined && combo > 1 && (
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full px-2 sm:px-3 py-0.5"
          >
            <span className="text-white font-bold text-[10px] sm:text-xs">
              x{combo} COMBO!
            </span>
          </motion.div>
        )}
      </div>

      {/* Right side - Pause button */}
      <button
        onClick={onPause}
        className="bg-black/50 rounded-full p-1.5 sm:p-2 pointer-events-auto active:scale-95 transition-transform touch-manipulation"
        aria-label="Pause game"
      >
        <Pause className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
      </button>

      {/* Power-up indicator - positioned for landscape */}
      {powerUp && powerUpTimeLeft && powerUpTimeLeft > 0 && (
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -50, opacity: 0 }}
          className="absolute top-12 sm:top-16 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full px-3 sm:px-4 py-1 sm:py-2 flex items-center gap-1.5 sm:gap-2"
        >
          <Zap className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-300" />
          <span className="text-white font-bold text-xs sm:text-sm">{powerUp}</span>
          <div className="w-12 sm:w-16 h-1.5 sm:h-2 bg-white/30 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-yellow-300"
              initial={{ width: '100%' }}
              animate={{ width: '0%' }}
              transition={{ duration: powerUpTimeLeft, ease: 'linear' }}
            />
          </div>
        </motion.div>
      )}
    </div>
  );
}
